import { useState, useEffect } from "react";
import { Link } from "react-router";
import { motion } from "motion/react";
import {
  ArrowRight, CheckCircle, ChevronRight,
  Phone, Clock, Wrench, Camera, Loader2
} from "lucide-react";
import { ImageWithFallback } from "./figma/ImageWithFallback";
import { servicesService, parseFeatures, type Service } from "../../lib/services/servicesService";
import * as Icons from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};
const stagger = { visible: { transition: { staggerChildren: 0.1 } } };

import { SEO } from "./SEO";

export function ServicesPage() {
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    servicesService.list()
      .then((data) => {
        const active = data.filter(s => s.status !== "Inactive");
        setServices(active.sort((a, b) => Number(a.sortOrder) - Number(b.sortOrder)));
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#1a2d50]/40" />
      </div>
    );
  }

  return (
    <div>
      <SEO
        title="Our Services | Solace SMART Solutions"
        description="Explore our wide range of engineering services including HVAC, Heat Pump water heaters, Industrial Refrigeration, and Electrical Solutions delivered with Japanese precision."
      />
      {/* Hero Banner */}
      <section className="relative py-24 bg-gradient-to-br from-[#1a2d50] via-[#1f3561] to-[#243d6a] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-10 w-96 h-96 bg-[#E9A319] rounded-full blur-3xl" />
          <div className="absolute bottom-10 left-10 w-72 h-72 bg-blue-300 rounded-full blur-3xl" />
        </div>
        <div className="max-w-screen-2xl mx-auto px-6 relative z-10 text-center">
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="inline-flex items-center justify-center gap-2 text-sm mb-6 w-full">
            <Link to="/" className="text-white/50 hover:text-white transition-colors">Home</Link>
            <ChevronRight className="w-3.5 h-3.5 text-white/30" />
            <span className="text-[#E9A319]" style={{ fontWeight: 600 }}>Our Services</span>
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-4xl md:text-5xl mt-3 mb-6" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800 }}>
            Complete Engineering Solutions for Every Need
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-blue-100 max-w-2xl mx-auto text-lg" style={{ lineHeight: 1.8 }}>
            From HVAC projects and heat pump water heaters to refrigeration, electrical panels, and India's 1st refrigerant ISO tank system — complete solutions delivered with Japanese precision.
          </motion.p>
        </div>
      </section>

      {/* Quick Links */}
      <section className="sticky top-16 z-40 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80 border-b border-border shadow-sm">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex gap-2 w-full overflow-x-auto pb-1 md:pb-0 [&::-webkit-scrollbar]:hidden" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            {services.map((s) => {
              const Icon = (Icons as any)[s.icon] || Icons.Settings;
              return (
                <a
                  key={s.$id}
                  href={`#${s.slug}`}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm whitespace-nowrap transition-all border shrink-0 bg-white border-border text-[#1a2d50] hover:bg-muted hover:border-[#1a2d50]/30"
                  style={{ fontWeight: 500 }}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {s.shortTitle}
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {/* Service Details */}
      <section className="py-20">
        <div className="max-w-screen-2xl mx-auto px-6 space-y-32">
          {services.map((service, index) => {
            const Icon = (Icons as any)[service.icon] || Icons.Settings;
            const feats = parseFeatures(service.features);

            return (
              <motion.div
                key={service.$id}
                id={service.slug}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={stagger}
                className={`grid lg:grid-cols-2 gap-16 items-center ${index % 2 === 1 ? "lg:direction-rtl" : ""}`}
                style={{ direction: "ltr" }}
              >
                <motion.div variants={fadeUp} className={index % 2 === 1 ? "lg:order-2" : ""}>
                  <div className="relative">
                    <ImageWithFallback
                      src={service.img}
                      alt={service.title}
                      className="w-full rounded-2xl shadow-xl"
                    />
                    <div className={`absolute -bottom-4 ${index % 2 === 1 ? "-left-4" : "-right-4"} p-4 rounded-2xl border hidden md:flex items-center gap-3 bg-white/95 backdrop-blur-sm shadow-sm`} style={{ borderColor: `${service.colorAccent || '#1a2d50'}20`, color: service.colorAccent || '#1a2d50' }}>
                      <Icon className="w-6 h-6" />
                      <span style={{ fontWeight: 700 }}>{service.shortTitle}</span>
                    </div>
                  </div>
                </motion.div>
                <motion.div variants={fadeUp} className={index % 2 === 1 ? "lg:order-1" : ""}>
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm mb-4 border bg-white/50 backdrop-blur-sm shadow-sm" style={{ borderColor: `${service.colorAccent || '#1a2d50'}30`, color: service.colorAccent || '#1a2d50' }}>
                    <Icon className="w-4 h-4" />
                    <span style={{ fontWeight: 600 }}>Service</span>
                  </div>
                  <h2 className="text-3xl text-[#1a2d50] mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800 }}>
                    {service.title}
                  </h2>
                  <p className="text-muted-foreground mb-8" style={{ lineHeight: 1.8 }}>{service.desc}</p>
                  <div className="grid sm:grid-cols-2 gap-3 mb-8">
                    {feats.map((f, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-[#E9A319] shrink-0 mt-0.5" />
                        <span className="text-sm text-foreground">{f}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Link
                      to={`/services/${service.slug}`}
                      className="inline-flex items-center justify-center gap-2 bg-[#E9A319] text-white px-6 py-3 rounded-full hover:bg-[#d4920f] transition-all"
                      style={{ fontWeight: 600 }}
                    >
                      Learn More <ArrowRight className="w-4 h-4" />
                    </Link>
                    <Link
                      to="/contact"
                      className="inline-flex items-center justify-center gap-2 border border-[#1a2d50]/15 text-[#1a2d50] px-6 py-3 rounded-full hover:bg-[#f8f6f1] transition-all"
                      style={{ fontWeight: 600 }}
                    >
                      <Phone className="w-4 h-4" /> Get a Quote
                    </Link>
                  </div>
                </motion.div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 bg-[#f8f6f1]">
        <div className="max-w-screen-2xl mx-auto px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-16">
            <motion.span variants={fadeUp} className="text-[#E9A319] text-sm tracking-widest uppercase" style={{ fontWeight: 600 }}>Our Process</motion.span>
            <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl text-[#1a2d50] mt-3 mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800 }}>
              How We Work
            </motion.h2>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid md:grid-cols-4 gap-8">
            {[
              { step: "01", icon: Phone, title: "Site Survey", desc: "We conduct a detailed site survey to understand your facility, requirements, and constraints before designing any solution." },
              { step: "02", icon: Wrench, title: "Design & Consultation", desc: "Our engineers prepare optimum cost designs with precise calculations, product specifications, and transparent pricing." },
              { step: "03", icon: Camera, title: "Installation", desc: "Our certified technicians execute installation following SQDMC standards — Safety, Quality, Delivery, Morale & Cost." },
              { step: "04", icon: Clock, title: "Commissioning & AMC", desc: "Full system commissioning, testing, handover training, and optional Annual Maintenance Contract for ongoing support." },
            ].map((item) => (
              <motion.div key={item.step} variants={fadeUp} className="text-center relative">
                <div className="w-16 h-16 bg-[#E9A319]/10 rounded-2xl flex items-center justify-center mx-auto mb-5 relative">
                  <item.icon className="w-7 h-7 text-[#E9A319]" />
                  <span className="absolute -top-2 -right-2 bg-[#E9A319] text-white w-7 h-7 rounded-full flex items-center justify-center text-xs" style={{ fontWeight: 700 }}>
                    {item.step}
                  </span>
                </div>
                <h3 className="text-lg text-[#1a2d50] mb-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700 }}>{item.title}</h3>
                <p className="text-muted-foreground text-sm" style={{ lineHeight: 1.7 }}>{item.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#E9A319]">
        <div className="max-w-screen-2xl mx-auto px-6 text-center">
          <Wrench className="w-12 h-12 text-white mx-auto mb-4" />
          <h2 className="text-3xl text-white mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800 }}>
            Do You Have a Project? Feel Free to Contact Us
          </h2>
          <p className="text-yellow-100 mb-8 max-w-xl mx-auto" style={{ lineHeight: 1.8 }}>
            Our expert team is ready to survey your site and deliver an optimum cost solution. No obligation — just expert engineering consultation.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-white text-[#E9A319] px-8 py-4 rounded-full hover:bg-gray-100 transition-all shadow-xl"
            style={{ fontWeight: 700, fontSize: "1.1rem" }}
          >
            <Phone className="w-5 h-5" /> Get a Free Quote
          </Link>
        </div>
      </section>
    </div>
  );
}